import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectorRef, HostListener, ElementRef, TemplateRef } from '@angular/core';
import { Subject } from 'rxjs';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { ToastrService } from 'ngx-toastr';
import { MatDialog } from '@angular/material/dialog';
import { WaterCapacitiesViewService, WaterCapacitiesRow, WaterCapacitiesDetailRequest } from '../../services/water-capacities-view.service';
import { TableColumn } from '../../services/interfaces/water-capacities-view.interfaces';
import { AuthService } from '../../services/auth.service';
import { ErrorHandler } from '../../utils/error-handler';
import { exportRowsToExcel } from '../../utils/excel-export';

@Component({
  selector: 'app-water-capacities-view',
  standalone: false,
  templateUrl: './water-capacities-view.component.html',
  styleUrls: ['./water-capacities-view.component.scss']
})
export class WaterCapacitiesViewComponent implements OnInit, OnDestroy {
  @ViewChild(CdkVirtualScrollViewport) viewport!: CdkVirtualScrollViewport;
  @ViewChild('floatingFilterPanel') floatingFilterPanel!: ElementRef;
  @ViewChild('deleteConfirmDialog') deleteConfirmDialog!: TemplateRef<any>;
  @ViewChild('exportOptionsDialog') exportOptionsDialog!: TemplateRef<any>;

  spinnerBoolean: boolean = false;
  data: WaterCapacitiesRow[] = [];
  selectedRows: WaterCapacitiesRow[] = [];
  showCancel: boolean = false;
  loading: boolean = false;
  hoveredRowIndex: number | null = null;

  // Pagination tracking
  nextRowNumber: number | null = 0;
  hasMore: boolean = true;

  // Dropdown options
  devAreaOptions: string[] = [];
  developmentAreaOptions: string[] = [];
  subDevelopmentAreaOptions: string[] = [];
  waterFacilityNameOptions: string[] = [];
  facilityTypeOptions: string[] = [];

  // Temporary selection for dev area dropdown
  tempDevAreaSelection: string = '';

  // Filter-related properties
  filterColumnsData: { [key: string]: string[] } = {};
  isLoadingFilterOptions: boolean = false;
  filterSearch: { [key: string]: string } = {};
  activeFilter: string = '';
  showFloatingFilter: boolean = false;
  dropdownPosition = { top: 0, left: 0 };
  multiFilters: { [key: string]: string[] } = {};
  currentFilters: { [key: string]: string | null } = {
    devArea: null,
    waterFacilityName: null,
    facilityType: null,
    capacity: null,
    comments: null,
  };

  // Date range filters for effective date
  dateRangeFilters: {
    startDate: string | null;
    endDate: string | null;
  } = {
    startDate: null,
    endDate: null,
  };

  // Capacity search filter
  capacitySearchFilter: string = '';

  // Comments search filter
  commentsSearchFilter: string = '';

  public dialogMessage: string = '';
  private destroy$ = new Subject<void>();

  columns: TableColumn[] = [
    { field: 'devArea', header: 'Dev Area', required: true, filterable: true, filterType: 'multi-select', allowMultiple: true },
    { field: 'waterFacilityName', header: 'Water Facility Name', required: true, filterable: true, filterType: 'multi-select' },
    { field: 'facilityType', header: 'Facility Type', required: false, filterable: true, filterType: 'multi-select' },
    { field: 'capacity', header: 'Capacity', required: true, filterable: true, filterType: 'search' },
    { field: 'effectiveDate', header: 'Effective Date', required: true, filterable: true, filterType: 'date-range' },
    { field: 'comments', header: 'Comments', required: false, filterable: true, filterType: 'search' }
  ];

  constructor(
    private toastr: ToastrService,
    private waterCapacitiesService: WaterCapacitiesViewService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef,
    private dialog: MatDialog
  ) {}

  async ngOnInit(): Promise<void> {
    await Promise.all([
      this.loadInitialDropdowns(),
      this.loadInitialFilterOptions(),
    ]);
    await this.loadWaterCapacitiesData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async loadInitialDropdowns(): Promise<void> {
    try {
      this.spinnerBoolean = true;
      const dropdownData = await this.waterCapacitiesService.getAllDropdownOptions({
        developmentArea: null,
        facilityName: null,
        facilityType: null,
      });
      
      this.applyDropdownOptions(dropdownData);

    } catch (error: any) {
      const errorMessage = error.message || 'Failed to load dropdown options';
      this.toastr.error(errorMessage, 'Error');
    } finally {
      this.spinnerBoolean = false;
    }
  }

  private setFilterColumnsData(devAreas: string[], waterFacilityNames: string[], facilityTypes: string[]): void {
    this.filterColumnsData['devArea'] = [...devAreas];
    this.filterColumnsData['waterFacilityName'] = [...waterFacilityNames];
    this.filterColumnsData['facilityType'] = [...facilityTypes];
  }

  async loadInitialFilterOptions(): Promise<void> {
    try {
      this.isLoadingFilterOptions = true;
      const filterData = await this.waterCapacitiesService.getFilterOptionsBySelection({
        developmentArea: null,
        facilityName: null,
        facilityType: null,
      });

      this.setFilterColumnsData(
        filterData.devAreas || [],
        filterData.waterFacilityNames || [],
        filterData.facilityTypes || []
      );
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to load filter options';
      this.toastr.error(errorMessage, 'Error');
    } finally {
      this.isLoadingFilterOptions = false;
    }
  }

  private async refreshDropdownOptionsFromCurrentSelection(): Promise<void> {
    const dropdownData = await this.waterCapacitiesService.getAllDropdownOptions({
      developmentArea: this.currentFilters['devArea'] || null,
      facilityName: this.currentFilters['waterFacilityName'] || null,
      facilityType: this.currentFilters['facilityType'] || null,
    });

    this.applyDropdownOptions(dropdownData, true);
  }

  private async refreshDropdownOptionsFromRowSelection(row: WaterCapacitiesRow): Promise<void> {
    const dropdownData = await this.waterCapacitiesService.getAllDropdownOptions({
      developmentArea: row.devArea || null,
      facilityName: row.waterFacilityName || null,
      facilityType: row.facilityType || null,
    });

    const selectedDevAreas = this.getSelectedDevAreas(row);
    this.applyDropdownOptions(dropdownData, true);

    // Keep selected dev areas available even if they are not present in cached master list.
    this.devAreaOptions = Array.from(new Set([...this.devAreaOptions, ...selectedDevAreas]))
      .filter(area => !!area && area.trim() !== '');
  }

  private applyDropdownOptions(dropdownData: {
    devAreas?: string[];
    developmentAreas?: string[];
    subDevelopmentAreas?: string[];
    waterFacilityNames?: string[];
    facilityTypes?: string[];
  }, preserveDevAreaOptions: boolean = false): void {
    const unique = (values: string[]) => Array.from(new Set(values.filter(v => !!v && v.trim() !== '')));

    const developmentAreas = unique(dropdownData.developmentAreas || []);
    const subDevelopmentAreas = unique(dropdownData.subDevelopmentAreas || []);
    const mergedDevAreas = unique([
      ...(dropdownData.devAreas || []),
      ...developmentAreas,
      ...subDevelopmentAreas,
    ]);

    if (!preserveDevAreaOptions || this.devAreaOptions.length === 0) {
      this.developmentAreaOptions = developmentAreas;
      this.subDevelopmentAreaOptions = subDevelopmentAreas;
      this.devAreaOptions = mergedDevAreas;
    }

    this.waterFacilityNameOptions = dropdownData.waterFacilityNames || [];
    this.facilityTypeOptions = dropdownData.facilityTypes || [];
  }

  private async refreshFilterOptionsFromCurrentSelection(): Promise<void> {
    this.isLoadingFilterOptions = true;
    try {
      const filterData = await this.waterCapacitiesService.getFilterOptionsBySelection({
        developmentArea: this.currentFilters['devArea'] || null,
        facilityName: this.currentFilters['waterFacilityName'] || null,
        facilityType: this.currentFilters['facilityType'] || null,
      });

      this.setFilterColumnsData(
        filterData.devAreas || [],
        filterData.waterFacilityNames || [],
        filterData.facilityTypes || []
      );
    } finally {
      this.isLoadingFilterOptions = false;
    }
  }

  private withCurrentValue(options: string[], value: string): string[] {
    if (!value) {
      return options;
    }
    return options.includes(value) ? options : [value, ...options];
  }

  getWaterFacilityNameOptions(row: WaterCapacitiesRow): string[] {
    return this.withCurrentValue(this.waterFacilityNameOptions, row.waterFacilityName);
  }

  getFacilityTypeOptions(row: WaterCapacitiesRow): string[] {
    return this.withCurrentValue(this.facilityTypeOptions, row.facilityType);
  }

  getDevelopmentAreaOptions(): string[] {
    return this.developmentAreaOptions;
  }

  getSubDevelopmentAreaOptions(): string[] {
    return this.subDevelopmentAreaOptions;
  }

  async loadWaterCapacitiesData(filters?: Partial<WaterCapacitiesDetailRequest>): Promise<void> {
    try {
      this.spinnerBoolean = true;
      
      const filterPayload: WaterCapacitiesDetailRequest = {
        devArea: this.currentFilters['devArea'] || null,
        waterFacilityName: this.currentFilters['waterFacilityName'] || null,
        facilityType: this.currentFilters['facilityType'] || null,
        capacity: this.currentFilters['capacity'] || null,
        startDate: this.dateRangeFilters.startDate || null,
        endDate: this.dateRangeFilters.endDate || null,
        comments: this.currentFilters['comments'] || null,
        lastRowNumber: filters?.lastRowNumber ?? 0,
        ...filters
      };

      const response = await this.waterCapacitiesService.getWaterCapacitiesDataWithPagination(filterPayload);
      
      this.nextRowNumber = response.nextRowNumber ?? null;
      this.hasMore = this.nextRowNumber !== null && this.nextRowNumber > filterPayload.lastRowNumber;

      if (response.data && response.data.length > 0) {
        let newRows = response.data.map((item): WaterCapacitiesRow => ({
          _rowId: this.generateRowId(),
          devArea: item.devArea,
          waterFacilityName: item.waterFacilityName,
          facilityType: item.facilityType,
          capacity: item.capacity,
          effectiveDate: item.effectiveDate,
          comments: item.comments,
          isNew: false,
          _rowEdited: false,
          original: {}
        }));

        for (const row of newRows) {
          row.original = { ...row };
        }

        if (filterPayload.lastRowNumber === 0) {
          this.data = newRows;
        } else {
          this.mergeRows(newRows);
        }

      } else {
        if (filterPayload.lastRowNumber === 0) {
          this.data = [];
          if (!this.hasAnyFilterApplied()) {
            this.toastr.info('No existing water capacities data found', 'Info');
          }
        }
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to load water capacities data';
      this.toastr.error(errorMessage, 'Error');
      this.hasMore = false;
    } finally {
      this.spinnerBoolean = false;
      this.loading = false;
    }
  }

  private mergeRows(newRows: WaterCapacitiesRow[]): void {
    const keyOf = (r: WaterCapacitiesRow) =>
      `${r.devArea}||${r.waterFacilityName}||${r.facilityType}||${r.capacity}||${r.effectiveDate}`;

    const existingKeys = new Set(this.data.map(keyOf));
    const toAdd = newRows.filter((r) => !existingKeys.has(keyOf(r)));
    this.data = [...this.data, ...toAdd];
  }

  loadFirstPage(): void {
    this.data = [];
    this.nextRowNumber = 0;
    this.hasMore = true;
    this.loadWaterCapacitiesData({ lastRowNumber: 0 });
  }

  onScrolledIndexChange(index: number): void {
    const buffer = 10;
    if (
      this.hasMore &&
      !this.loading &&
      !this.spinnerBoolean &&
      index >= Math.max(0, this.data.length - buffer)
    ) {
      this.loading = true;
      this.loadWaterCapacitiesData({ lastRowNumber: this.nextRowNumber ?? 0 });
    }
  }

  async addRow(): Promise<void> {
    await this.loadInitialDropdownsForNewRow();

    const newRow: WaterCapacitiesRow = { 
      _rowId: this.generateRowId(), 
      devArea: '', 
      waterFacilityName: '', 
      facilityType: '',
      capacity: null,
      effectiveDate: '', 
      comments: '',
      isNew: true, 
      _rowEdited: false, 
      original: {} 
    };
    
    this.data = [newRow, ...this.data];
    this.showCancel = true;
    this.clearSelection();
    
    this.cdr.detectChanges();
    setTimeout(() => {
      this.viewport?.checkViewportSize();
      this.viewport?.scrollToIndex(0, 'smooth');
    });
    
    this.toastr.info('New water capacities row added. Click Cancel to discard or Save to keep changes.');
  }

  private async loadInitialDropdownsForNewRow(): Promise<void> {
    try {
      this.spinnerBoolean = true;
      const dropdownData = await this.waterCapacitiesService.getAllDropdownOptions({
        developmentArea: null,
        facilityName: null,
        facilityType: null,
      });

      this.applyDropdownOptions(dropdownData);
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to load dropdown options';
      this.toastr.error(errorMessage, 'Error');
    } finally {
      this.spinnerBoolean = false;
    }
  }

  copyRow(index: number): void {
    const originalRow = this.data[index];
    if (!originalRow) return;

    const copiedRow: WaterCapacitiesRow = {
      _rowId: this.generateRowId(),
      devArea: originalRow.devArea,
      waterFacilityName: originalRow.waterFacilityName,
      facilityType: originalRow.facilityType,
      capacity: originalRow.capacity,
      effectiveDate: '',
      comments: originalRow.comments,
      isNew: true,
      _rowEdited: false,
      original: {}
    };

    this.data = [copiedRow, ...this.data];
    this.showCancel = true;
    this.clearSelection();

    this.cdr.detectChanges();
    setTimeout(() => {
      this.viewport?.checkViewportSize();
      this.viewport?.scrollToIndex(0, 'smooth');
    }, 100);

    this.toastr.info('Row copied successfully. Click Cancel to discard or Save to keep changes.');
  }

  async handleSave(): Promise<void> {
    const userName = this.authService.getUserName();
    let successCount = 0;
    let failureCount = 0;
    let errorMessages: string[] = [];

    try {
      const required = this.columns.filter(col => col.required).map(col => col.field);
      const changedRows = this.data.filter(row => row.isNew || row._rowEdited);
      
      if (changedRows.length === 0) {
        this.toastr.info('No changes to save.');
        return;
      }

      const invalid = changedRows.filter(row => 
        required.some(field => {
          const value = row[field];
          return value === undefined || value === null || value === '';
        })
      );
      
      if (invalid.length) { 
        this.toastr.error('Please fill all required fields before saving.'); 
        return; 
      }

      this.spinnerBoolean = true;
      
      for (const row of changedRows) {
        const action: 'insert' | 'update' = row.isNew ? 'insert' : 'update';
        
        const response = await this.waterCapacitiesService.saveWaterCapacities(row, action, userName);
        const parsed = ErrorHandler.parseApiResult(response);

        if (parsed.success) {
          row.isNew = false;
          row._rowEdited = false;
          row.original = { ...row };
          successCount++;
        } else {
          failureCount++;
          errorMessages.push(parsed.message || 'Unknown error occurred');
        }
      }

      await this.loadWaterCapacitiesData();

      if (successCount > 0) {
        this.toastr.success(`${successCount} water capacities row(s) saved successfully`, 'Success');
        this.showCancel = false;
      }

      if (failureCount > 0) {
        const detailedErrorMsg = errorMessages.length > 0
          ? errorMessages.join('<br>')
          : `${failureCount} water capacities row(s) failed to save`;
        
        this.toastr.error(detailedErrorMsg, 'Save Error', {
          timeOut: 8000,
          enableHtml: true
        });
      }
    } catch (error: any) {
      const parsed = ErrorHandler.parseApiResult(error?.error || error);
      this.toastr.error(parsed.message || 'Failed to save water capacities data', 'Error');
    } finally {
      this.spinnerBoolean = false;
    }
  }

  async handleDelete(): Promise<void> {
    if (!this.selectedRows.length) { 
      this.toastr.info('Please select rows to delete.'); 
      return; 
    }

    const userName = this.authService.getUserName();

    this.dialogMessage = `Are you sure you want to delete ${this.selectedRows.length} row(s)? This action cannot be undone.`;

    const dialogRef = this.dialog.open(this.deleteConfirmDialog, {
      width: '400px',
      disableClose: true,
    });

    const confirmed = await dialogRef.afterClosed().toPromise();
    if (!confirmed) return;

    let successCount = 0;
    let failureCount = 0;
    let errorMessages: string[] = [];

    try {
      this.spinnerBoolean = true;

      for (const row of this.selectedRows) {
        if (row.isNew) {
          const index = this.data.indexOf(row);
          if (index > -1) {
            this.data.splice(index, 1);
          }
          successCount++;
        } else {
          const response = await this.waterCapacitiesService.deleteWaterCapacities(row, userName);
          const parsed = ErrorHandler.parseApiResult(response);

          if (parsed.success) {
            successCount++;
          } else {
            failureCount++;
            errorMessages.push(parsed.message || 'Unknown error occurred');
          }
        }
      }

      this.data = this.data.filter(row => !this.selectedRows.includes(row));
      this.selectedRows = [];

      await this.loadWaterCapacitiesData();
      
      if (successCount > 0) {
        this.toastr.success(`${successCount} row(s) deleted successfully.`);
      }
      if (failureCount > 0) {
        const detailedErrorMsg = errorMessages.length > 0
          ? errorMessages.join('<br>')
          : `${failureCount} row(s) failed to delete`;
        
        this.toastr.error(detailedErrorMsg, 'Delete Error', {
          timeOut: 8000,
          enableHtml: true
        });
      }
    } catch (error: any) {
      const parsed = ErrorHandler.parseApiResult(error?.error || error);
      this.toastr.error(parsed.message || 'Failed to delete water capacities data', 'Error');
    } finally {
      this.spinnerBoolean = false;
    }
  }

  cancelRow(): void {
    this.data = this.data.filter((row) => !row.isNew);
    
    this.data.forEach(row => {
      if (row._rowEdited && row.original && Object.keys(row.original).length > 0) {
        Object.assign(row, row.original);
        row._rowEdited = false;
      }
    });
    
    this.showCancel = false;
    this.clearSelection();
    
    this.cdr.detectChanges();
    
    setTimeout(() => {
      this.viewport?.checkViewportSize();
    });
    
    this.toastr.info('Changes cancelled.');
  }

  clearSelection(): void {
    this.selectedRows = [];
  }

  toggleRowSelection(row: WaterCapacitiesRow, ev: Event): void {
    const target = ev.target as HTMLInputElement;
    if (target.checked) { 
      if (!this.selectedRows.includes(row)) this.selectedRows.push(row); 
    } else { 
      const index = this.selectedRows.indexOf(row); 
      if (index > -1) this.selectedRows.splice(index, 1); 
    }
  }

  markAsEdited(row: WaterCapacitiesRow): void {
    if (!row.original || Object.keys(row.original).length === 0) {
      row.original = { ...row };
    }
    if (!row.isNew) {
      row._rowEdited = true;
      this.showCancel = true;
    }
  }

  handleEdit(row: WaterCapacitiesRow): void { 
    this.markAsEdited(row);
  }

  isIdentityFieldEditable(row: WaterCapacitiesRow): boolean {
    return row.isNew;
  }

  async onWaterFacilitySelectionChange(row: WaterCapacitiesRow): Promise<void> {
    if (!this.isIdentityFieldEditable(row)) {
      return;
    }

    this.handleEdit(row);
    try {
      await this.refreshDropdownOptionsFromRowSelection(row);
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to refresh dropdown options';
      this.toastr.error(errorMessage, 'Error');
    }
  }

  async onFacilityTypeSelectionChange(row: WaterCapacitiesRow): Promise<void> {
    if (!this.isIdentityFieldEditable(row)) {
      return;
    }

    this.handleEdit(row);
    try {
      await this.refreshDropdownOptionsFromRowSelection(row);
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to refresh dropdown options';
      this.toastr.error(errorMessage, 'Error');
    }
  }

  validateCapacity(row: WaterCapacitiesRow, ev: Event): void {
    const target = ev.target as HTMLInputElement;
    const value = parseFloat(target.value);
    
    if (isNaN(value) || value < 0) {
      row.capacity = null;
      this.toastr.warning('Capacity must be a positive number');
    } else {
      row.capacity = value;
    }
    
    this.markAsEdited(row);
  }

  async openFloatingFilter(event: MouseEvent, field: string): Promise<void> {
    event.stopPropagation();

    if (this.showFloatingFilter && this.activeFilter === field) {
      this.closeFloatingFilter();
      return;
    }

    if (this.getFilterType(field) === 'multi-select') {
      try {
        await this.refreshFilterOptionsFromCurrentSelection();
      } catch (error: any) {
        const errorMessage = error.message || 'Failed to refresh filter options';
        this.toastr.error(errorMessage, 'Error');
      }
    }

    const target = event.target as HTMLElement;
    const rect = target.getBoundingClientRect();
    this.dropdownPosition = {
      top: rect.bottom - 130+ window.scrollY,
      left: rect.left - 90 + window.scrollX,
    };
    this.activeFilter = field;
    this.showFloatingFilter = true;
  }

  closeFloatingFilter(): void {
    this.showFloatingFilter = false;
    this.activeFilter = '';
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.showFloatingFilter) {
      const target = event.target as HTMLElement;
      const clickedInside = this.floatingFilterPanel?.nativeElement.contains(target);
      const clickedOnFilterIcon = target.classList.contains('filter-icon') ||
        target.parentElement?.classList.contains('filter-icon');

      if (!clickedInside && !clickedOnFilterIcon) {
        this.closeFloatingFilter();
      }
    }
  }

  getFilterOptions(field: string): string[] {
    if (this.filterColumnsData[field]) {
      const options = this.filterColumnsData[field] || [];
      const search = (this.filterSearch[field] || '').toLowerCase();
      const filtered = options.filter(opt => opt && String(opt).toLowerCase().includes(search));
      return filtered;
    }
    return [];
  }

  toggleMultiFilter(field: string, value: string, event: any): void {
    if (!this.multiFilters[field]) this.multiFilters[field] = [];
    const isChecked = event.target.checked;
    if (isChecked) {
      if (!this.multiFilters[field].includes(value))
        this.multiFilters[field].push(value);
    } else {
      this.multiFilters[field] = this.multiFilters[field].filter(v => v !== value);
    }
  }

  isChecked(field: string, value: string): boolean {
    return this.multiFilters[field]?.includes(value) || false;
  }

  getFilterType(field: string): 'multi-select' | 'date-range' | 'search' {
    const column = this.columns.find(col => col.field === field);
    return column?.filterType || 'multi-select';
  }

  async applyFilter(field: string): Promise<void> {
    const filterType = this.getFilterType(field);

    if (filterType === 'multi-select') {
      if (this.multiFilters[field]?.length === 1) {
        this.currentFilters[field] = this.multiFilters[field][0];
      } else if (this.multiFilters[field]?.length > 1) {
        this.currentFilters[field] = this.multiFilters[field].join(',');
      } else {
        this.currentFilters[field] = null;
      }
    }

    this.showFloatingFilter = false;
    this.activeFilter = '';

    if (field === 'devArea' || field === 'waterFacilityName' || field === 'facilityType') {
      try {
        await this.refreshFilterOptionsFromCurrentSelection();
        await this.refreshDropdownOptionsFromCurrentSelection();
      } catch (error: any) {
        const errorMessage = error.message || 'Failed to refresh options';
        this.toastr.error(errorMessage, 'Error');
      }
    }

    this.loadFirstPage();
  }

  applyDateRangeFilter(): void {
    this.showFloatingFilter = false;
    this.activeFilter = '';
    this.loadFirstPage();
  }

  clearDateRangeFilter(): void {
    this.dateRangeFilters.startDate = null;
    this.dateRangeFilters.endDate = null;
    this.showFloatingFilter = false;
    this.activeFilter = '';
    this.loadFirstPage();
  }

  applyCapacitySearch(): void {
    this.currentFilters['capacity'] = this.capacitySearchFilter || null;
    this.showFloatingFilter = false;
    this.activeFilter = '';
    this.loadFirstPage();
  }

  clearCapacitySearch(): void {
    this.capacitySearchFilter = '';
    this.currentFilters['capacity'] = null;
    this.showFloatingFilter = false;
    this.activeFilter = '';
    this.loadFirstPage();
  }

  applyCommentsSearch(): void {
    this.currentFilters['comments'] = this.commentsSearchFilter || null;
    this.showFloatingFilter = false;
    this.activeFilter = '';
    this.loadFirstPage();
  }

  clearCommentsSearch(): void {
    this.commentsSearchFilter = '';
    this.currentFilters['comments'] = null;
    this.showFloatingFilter = false;
    this.activeFilter = '';
    this.loadFirstPage();
  }

  async clearFilter(field: string): Promise<void> {
    const filterType = this.getFilterType(field);

    if (filterType === 'date-range') {
      this.clearDateRangeFilter();
      return;
    } else if (filterType === 'search') {
      if (field === 'capacity') {
        this.clearCapacitySearch();
      } else if (field === 'comments') {
        this.clearCommentsSearch();
      }
      return;
    }

    this.multiFilters[field] = [];
    this.filterSearch[field] = '';
    this.currentFilters[field] = null;

    this.showFloatingFilter = false;
    this.activeFilter = '';

    if (field === 'devArea' || field === 'waterFacilityName' || field === 'facilityType') {
      try {
        await this.refreshFilterOptionsFromCurrentSelection();
        await this.refreshDropdownOptionsFromCurrentSelection();
      } catch (error: any) {
        const errorMessage = error.message || 'Failed to refresh options';
        this.toastr.error(errorMessage, 'Error');
      }
    }

    this.loadFirstPage();
  }

  hasAnyFilterApplied(): boolean {
    const hasCurrentFilter = Object.values(this.currentFilters).some(value => value !== null);
    const hasDateFilter = this.dateRangeFilters.startDate !== null || this.dateRangeFilters.endDate !== null;
    return hasCurrentFilter || hasDateFilter;
  }

  async clearAllFilters(): Promise<void> {
    this.multiFilters = {};
    this.filterSearch = {};
    this.dateRangeFilters.startDate = null;
    this.dateRangeFilters.endDate = null;
    this.capacitySearchFilter = '';
    this.commentsSearchFilter = '';

    Object.keys(this.currentFilters).forEach(key => {
      this.currentFilters[key] = null;
    });

    this.activeFilter = '';
    this.showFloatingFilter = false;

    try {
      await this.refreshFilterOptionsFromCurrentSelection();
      await this.refreshDropdownOptionsFromCurrentSelection();
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to refresh options';
      this.toastr.error(errorMessage, 'Error');
    }

    this.loadFirstPage();
  }

  async exportToCSV(): Promise<void> {
    if (!this.data.length) {
      this.toastr.info('No data available to export.');
      return;
    }

    const worksheetData = this.data.map((row: WaterCapacitiesRow) => {
      const exportRow: Record<string, any> = {};

      this.columns.forEach((col) => {
        const value = row[col.field];
        exportRow[col.header] = Array.isArray(value) ? value.join(', ') : value ?? '';
      });

      return exportRow;
    });

    await exportRowsToExcel(
      worksheetData,
      this.columns.map((col) => col.header),
      'WaterCapacitiesViewData.xlsx',
      'WaterCapacitiesView'
    );
  }

  openExportDialog(): void {
    this.dialog.open(this.exportOptionsDialog, {
      width: '380px',
      disableClose: true,
    });
  }

  async downloadAllData(): Promise<void> {
    try {
      const { blob, fileName } = await this.waterCapacitiesService.exportAllWaterCapacitiesData();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);
      this.toastr.success('Export downloaded successfully.');
    } catch (error: any) {
      this.toastr.error(error?.message || 'Failed to download export.');
    }
  }

  isFilterActive(field: string): boolean {
    const filterType = this.getFilterType(field);
    
    if (filterType === 'multi-select') {
      return (this.multiFilters[field] || []).length > 0;
    } else if (filterType === 'date-range') {
      return this.dateRangeFilters.startDate !== null || this.dateRangeFilters.endDate !== null;
    } else if (filterType === 'search') {
      if (field === 'capacity') {
        return this.capacitySearchFilter !== '';
      } else if (field === 'comments') {
        return this.commentsSearchFilter !== '';
      }
    }
    
    return false;
  }

  generateRowId(): string { 
    return `row_${Date.now()}_${Math.random().toString(36).substr(2,9)}`; 
  }

  // Multi-select Dev Area methods
  getSelectedDevAreas(row: WaterCapacitiesRow): string[] {
    if (!row.devArea) return [];
    return row.devArea.split(',').filter(area => area.trim() !== '');
  }

  async addDevArea(row: WaterCapacitiesRow, event: Event): Promise<void> {
    if (!this.isIdentityFieldEditable(row)) {
      return;
    }

    const selected = (event.target as HTMLSelectElement).value;
    if (selected && !this.getSelectedDevAreas(row).includes(selected)) {
      const currentAreas = this.getSelectedDevAreas(row);
      currentAreas.push(selected);
      row.devArea = currentAreas.join(',');
      this.handleEdit(row);

      try {
        await this.refreshDropdownOptionsFromRowSelection(row);
      } catch (error: any) {
        const errorMessage = error.message || 'Failed to refresh dropdown options';
        this.toastr.error(errorMessage, 'Error');
      }
    }
    (event.target as HTMLSelectElement).value = '';
  }

  async removeDevArea(row: WaterCapacitiesRow, areaToRemove: string): Promise<void> {
    if (!this.isIdentityFieldEditable(row)) {
      return;
    }

    const currentAreas = this.getSelectedDevAreas(row);
    const updatedAreas = currentAreas.filter(area => area !== areaToRemove);
    row.devArea = updatedAreas.join(',');
    this.handleEdit(row);

    try {
      await this.refreshDropdownOptionsFromRowSelection(row);
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to refresh dropdown options';
      this.toastr.error(errorMessage, 'Error');
    }
  }
}